import useI18n from "@/i18n";
import { buildTrainingPlanDrawerEntries, ITrainingPlanEntry } from "@Components/training-plans/training-plans-drawer/training-plans-drawer.utils";
import { AppContext } from "@Context/common/general/context";
import { useDialogManager } from "@Context/common/general/dialog-manager-context";
import { trainingPlansEvents } from "@Context/game/training-plans/training-plans-events";
import { useTrainingPlansProgressContext } from "@Context/game/training-plans/training-plans-progress-context";
import AnalyticsEvents from "@Enums/AnalyticsEvents";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { TRAINING_PLANS_ONBOARDING_DIALOG, TrainingPlansOnboardingStep } from "@hooks/game/training-plans/training-plans-shared";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

export interface IUseTrainingPlansDrawerReturn {
  closeDrawer: () => void;
  handleEdit: () => void;
  handleEntryClick: (entry: ITrainingPlanEntry) => void;
  isBuildingPlan: boolean;
  isDrawerOpen: boolean;
  isLoading: boolean;
  entries: ITrainingPlanEntry[];
}

/**
 * Drawer controller hook for Training Plans.
 *
 * Responsibility:
 * - Owns drawer open/close state and consumes shared progress state.
 * - Builds drawer UI model and exposes drawer interaction handlers.
 * - Redirects to onboarding when progress fetch indicates no active enrollment.
 */
export const useTrainingPlansDrawer = () => {
  const { state } = useContext(AppContext);
  const { i18n } = useI18n();
  const { track } = useAnalytics();
  const { openDialog } = useDialogManager();
  const { canUseTrainingPlans, isProgressResolved, progressData, progressError, refreshProgress, trainingPlanModel } = useTrainingPlansProgressContext();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isOpeningDrawer, setIsOpeningDrawer] = useState(false);
  const [isBuildingPlan, setIsBuildingPlan] = useState(false);
  const hasTrackedOpenedRef = useRef(false);

  const openOnboarding = useCallback(
    async (options?: { step?: TrainingPlansOnboardingStep; isEditMode?: boolean }) => {
      openDialog(TRAINING_PLANS_ONBOARDING_DIALOG, {
        isEditMode: options?.isEditMode ?? false,
        step: options?.step ?? TrainingPlansOnboardingStep.Intro,
      });
    },
    [openDialog]
  );

  const openDrawer = useCallback(
    ({ minimumLoadingMs = 0 }: { minimumLoadingMs?: number } = {}) => {
      if (!canUseTrainingPlans) {
        return;
      }

      const isBuilding = minimumLoadingMs > 0;
      setIsBuildingPlan(isBuilding);
      if (isBuilding) {
        setIsDrawerOpen(true);
      }
      setIsOpeningDrawer(true);

      const minimumLoadingTime =
        minimumLoadingMs > 0 ? new Promise<void>(resolve => window.setTimeout(resolve, minimumLoadingMs)) : Promise.resolve();

      void Promise.all([refreshProgress(), minimumLoadingTime])
        .then(([nextProgress]) => {
          setIsOpeningDrawer(false);
          setIsBuildingPlan(false);

          if (nextProgress?.enrollment?.is_active) {
            setIsDrawerOpen(true);
            return;
          }

          setIsDrawerOpen(false);
          void openOnboarding();
        })
        .catch(() => {
          setIsDrawerOpen(false);
          setIsOpeningDrawer(false);
          setIsBuildingPlan(false);
        });
    },
    [canUseTrainingPlans, openOnboarding, refreshProgress]
  );

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setIsOpeningDrawer(false);
    setIsBuildingPlan(false);
  }, []);

  const handleEdit = useCallback(() => {
    track(AnalyticsEvents.training_plan_edit_clicked);
    setIsDrawerOpen(false);
    void openOnboarding({ isEditMode: true, step: TrainingPlansOnboardingStep.Profile });
  }, [openOnboarding]);

  const handleEntryClick = useCallback(
    (entry: ITrainingPlanEntry) => {
      track(AnalyticsEvents.training_plan_entry_clicked);
      closeDrawer();
      entry.onClick?.();
    },
    [closeDrawer]
  );

  useEffect(() => {
    const unsubscribe = trainingPlansEvents.onOpenDrawer(openDrawer);
    return unsubscribe;
  }, [openDrawer]);

  useEffect(() => {
    if (isOpeningDrawer || !isDrawerOpen || !isProgressResolved || progressError || progressData?.enrollment?.is_active) {
      return;
    }

    setIsDrawerOpen(false);
    openDialog(TRAINING_PLANS_ONBOARDING_DIALOG, { step: TrainingPlansOnboardingStep.Intro, isEditMode: false });
  }, [isDrawerOpen, isOpeningDrawer, isProgressResolved, openDialog, progressData, progressError]);

  useEffect(() => {
    if (!canUseTrainingPlans) {
      setIsDrawerOpen(false);
      setIsOpeningDrawer(false);
      setIsBuildingPlan(false);
    }
  }, [canUseTrainingPlans]);

  useEffect(() => {
    if (!isDrawerOpen) {
      hasTrackedOpenedRef.current = false;
      return;
    }

    if (isProgressResolved && trainingPlanModel && !hasTrackedOpenedRef.current) {
      track(AnalyticsEvents.training_plan_opened);
      hasTrackedOpenedRef.current = true;
    }
  }, [trainingPlanModel, isDrawerOpen, isProgressResolved]);

  useEffect(() => {
    setIsDrawerOpen(false);
    setIsOpeningDrawer(false);
    setIsBuildingPlan(false);
  }, [state.account.id, state.user.id]);

  return {
    closeDrawer,
    handleEdit,
    handleEntryClick,
    isBuildingPlan,
    isDrawerOpen,
    isLoading: isDrawerOpen && (isOpeningDrawer || !isProgressResolved),
    entries: buildTrainingPlanDrawerEntries(progressData, trainingPlanModel, i18n)
  } satisfies IUseTrainingPlansDrawerReturn;
};
